/**
 * Encode configuration to binary format with query parameters
 */

import { ariStr2Bin, writeEncodedATI } from './ariStr2Bin';
import { encodeBase64Url } from './bufferUtils';
import {
	DELIMITERS,
	MENU_ITEM_TYPE_MAP,
	PERSONALIZE_MODE_MAP,
	SCHEMA_VERSION,
	SECTION_TYPE_MAP,
} from './constants';
import type {
	AppMenuConfig,
	EncodedConfig,
	MenuItem,
	MenuSection,
	PersonalizeMode,
	SuggestionSection,
	UnknownComponents,
} from './types';

// =============================================================================
// Configuration Validation
// =============================================================================

const MAX_SECTIONS = 15;

function validateConfig(config: AppMenuConfig): void {
	if (!config || typeof config !== 'object') {
		throw new Error('Config must be an object');
	}
	if (!Array.isArray(config.appMenuConfig)) {
		throw new Error('Config sections must be an array');
	}
	if (config.appMenuConfig.length === 0) {
		throw new Error('Cannot encode empty sections array');
	}
	if (config.appMenuConfig.length > MAX_SECTIONS) {
		throw new Error(`Too many sections (max ${MAX_SECTIONS})`);
	}
}

// =============================================================================
// ATI Encoding
// =============================================================================

/**
 * Encode a single ATI string to 3-byte binary format, then Base64URL
 *
 * @param ati - ATI string to encode
 * @param unknowns - Accumulator for unknown ATI components
 * @returns Base64URL-encoded ATI (4 characters)
 */
function encodeATIToBase64(ati: string, unknowns: UnknownComponents): string {
	const encoded = ariStr2Bin(ati, unknowns);
	const buffer = new Uint8Array(3);
	writeEncodedATI({ encoded, buffer, offset: 0 });
	return encodeBase64Url(buffer);
}

/**
 * Encode a list of ATI strings to comma-separated Base64URL format
 *
 * @param atiList - Array of ATI strings (optional)
 * @param unknowns - Accumulator for unknown ATI components
 * @returns Comma-separated Base64URL-encoded ATIs, or empty string if no ATIs
 */
function encodeATIList(atiList: string[] | undefined, unknowns: UnknownComponents): string {
	if (!atiList || atiList.length === 0) {
		return '';
	}
	return atiList.map((ati) => encodeATIToBase64(ati, unknowns)).join(',');
}

// =============================================================================
// Encoding Utilities
// =============================================================================

/**
 * Join array parts with hierarchical delimiter, truncating trailing empty/undefined values
 *
 * This utility implements a common pattern across encoding functions:
 * 1. Construct an array with known data
 * 2. Truncate array at last non-empty item
 * 3. Join array as string with delimiter at specified hierarchy level
 *
 * @param parts - Array of values to join (string | number | undefined)
 * @param delimiterLevel - Hierarchy level of delimiter to use (0=~, 1=|, 2=,, 3=:)
 * @returns Joined string with trailing empties removed
 *
 * @example
 * joinWithTruncation(['a', 'b', ''], 3) // 'a:b' (level 3 = ':')
 * joinWithTruncation(['a', '', ''], 2) // 'a' (level 2 = ',')
 * joinWithTruncation([1, 2, undefined], 1) // '1|2' (level 1 = '|')
 */
function joinWithTruncation(
	parts: (string | number | undefined)[],
	delimiterLevel: number,
): string {
	// When delimiter level is out of bounds, default to '~'
	const delimiter = DELIMITERS[delimiterLevel] ?? '~';

	// Find last index where value is not empty string or undefined
	let lastIndex = parts.length - 1;
	while (lastIndex >= 0 && (parts[lastIndex] === '' || parts[lastIndex] === undefined)) {
		lastIndex--;
	}
	// Slice to last non-empty index + 1, convert all to string, and join
	return parts
		.slice(0, lastIndex + 1)
		.map(String)
		.join(delimiter);
}

// =============================================================================
// Menu Item Encoding
// =============================================================================

const PROMOTED_FLAG = '1';

/**
 * Encode a single menu item
 *
 * Format: atiBase64:itemType[:promoted]
 * - The promoted flag is only included when true
 *
 * @param item - Menu item to encode
 * @param unknowns - Accumulator for unknown ATI components
 * @returns Encoded menu item string
 * @throws Error if item type is unknown
 */
function encodeMenuItem(item: MenuItem, unknowns: UnknownComponents): string {
	// Note: order of fields is important, do not change it without updating the decode function
	const typeCode = item.type !== undefined ? getCodeByType(item) : undefined;
	const atiBase64 = encodeATIToBase64(item.ati, unknowns);
	const isPromoted = item.promoted === true;
	const promotedFlag = isPromoted ? PROMOTED_FLAG : undefined;

	return joinWithTruncation([atiBase64, typeCode, promotedFlag], 3); // Level 3: ':' (item properties)
}

/**
 * Get the numeric type code for a menu item
 *
 * @param item - Menu item
 * @returns Numeric type code
 * @throws Error if item type is unknown or undefined
 */
function getCodeByType(item: MenuItem): number {
	// If go down the path of getting code, type should not be undefined
	if (item.type === undefined) {
		throw new Error('Item type is undefined');
	}

	const typeCode = MENU_ITEM_TYPE_MAP[item.type];
	if (typeCode === undefined) {
		throw new Error(`Unknown item type: ${item.type}`);
	}

	return typeCode;
}

/**
 * Get the numeric code for a menu section
 *
 * @param sectionType - Section type MenuSection['type']
 * @returns String type code number
 */
function sectionTypeToCode(sectionType: MenuSection['type']): number {
	const typeCode = SECTION_TYPE_MAP[sectionType];
	if (typeCode === undefined) {
		throw new Error(`Unknown section type: ${String(sectionType)}`);
	}

	return typeCode;
}

// =============================================================================
// Section Encoding
// =============================================================================

/**
 * Get the numeric code for a personalize mode
 *
 * @param mode - Personalize mode
 * @returns Numeric code for the mode
 * @throws Error if mode is unknown
 */
function personalizeCodeByMode(mode: PersonalizeMode): number {
	const code = PERSONALIZE_MODE_MAP[mode];
	if (code === undefined) {
		throw new Error(`Unknown personalize mode: ${mode}`);
	}
	return code;
}

/**
 * Encode an overflow threshold
 *
 * @param overflowThreshold - Overflow threshold
 * @returns Encoded overflow threshold string
 */
function encodeOverflowThreshold(overflowThreshold: number): string {
	if (overflowThreshold < 0) {
		throw new Error('Overflow threshold must be positive');
	}
	return overflowThreshold.toString();
}

/**
 * Encode an app-defined section
 *
 * Format: 0|item1,item2,...|personalizeCode
 * - Trailing default values are truncated (personalize='0' / 'off' can be omitted)
 * - personalizeCode: 0=off, 1=full, 2=overflow
 *
 * @private
 * @param section - App-defined section to encode
 * @param unknowns - Accumulator for unknown ATI components
 * @returns Encoded section string
 */
function encodeAppDefinedSection(
	section: { items: MenuItem[]; overflowThreshold?: number; personalize?: PersonalizeMode; },
	unknowns: UnknownComponents,
): string {
	// Note: order of fields is important, do not change it without updating the decode function
	const sectionType = sectionTypeToCode('appDefinedSection');
	const encodedItems = section.items.map((item) => encodeMenuItem(item, unknowns)).join(',');
	const personalizeCode =
		section.personalize && section.personalize !== 'off'
			? personalizeCodeByMode(section.personalize)
			: undefined;
	const overflowThreshold =
		personalizeCode !== undefined
			&& section.personalize === 'overflow'
			&& section.overflowThreshold !== undefined
			? encodeOverflowThreshold(section.overflowThreshold)
			: undefined;
	return joinWithTruncation([sectionType, encodedItems, personalizeCode, overflowThreshold], 1); // Level 1: '|' (section fields)
}

/**
 * Encode a suggestion section
 *
 * Format: 1|limit|excludeAti1,excludeAti2,...|preferredAti1,preferredAti2,...
 * - Trailing empty values are truncated (empty excludeAtis/preferredAtis can be omitted)
 * - Default limit (5) is included explicitly for clarity
 *
 * @private
 * @param section - Suggestion section to encode
 * @param unknowns - Accumulator for unknown ATI components
 * @returns Encoded section string
 */
function encodeSuggestionSection(section: SuggestionSection, unknowns: UnknownComponents): string {
	// Note: order of fields is important, do not change it without updating the decode function
	const sectionType = sectionTypeToCode('suggestionSection');
	const limit = section.limit;
	const excludeAtis = encodeATIList(section.excludeAtis, unknowns);
	const preferredAtis = encodeATIList(section.preferredAtis, unknowns);

	return joinWithTruncation([sectionType, limit, excludeAtis, preferredAtis], 1); // Level 1: '|' (section fields)
}

/**
 * Encode a single menu section based on its type
 *
 * @param section - Menu section to encode
 * @param unknowns - Accumulator for unknown ATI components
 * @returns Encoded section string
 * @throws Error if section type is unknown
 */
function encodeSection(section: MenuSection, unknowns: UnknownComponents): string {
	switch (section.type) {
		case 'appDefinedSection':
			return encodeAppDefinedSection(section, unknowns);
		case 'suggestionSection':
			return encodeSuggestionSection(section, unknowns);
		default:
			// TypeScript exhaustiveness check - this should never happen
			throw new Error(`Unknown section type: ${String(section)}`);
	}
}

// =============================================================================
// Final Assembly
// =============================================================================

/**
 * Build the raw configuration string from encoded sections
 *
 * Format: v{SCHEMA_VERSION}~section1~section2~...
 *
 * @private
 * @param encodedSections - Array of encoded section strings
 * @returns Raw configuration string with version prefix
 */
function buildRawConfigString(encodedSections: string[]): string {
	return `v${SCHEMA_VERSION}~${encodedSections.join('~')}`;
}

/**
 * Attach unknown component query parameters to the encoded result
 *
 * @private
 * @param encodedData - Base64URL-encoded configuration data
 * @param unknowns - Unknown ATI components collected during encoding
 * @returns Encoded configuration with query parameters
 */
function attachQueryParameters(encodedData: string, unknowns: UnknownComponents): EncodedConfig {
	const result: EncodedConfig = { d: encodedData };

	if (unknowns.owners.length > 0) {
		result.ro = unknowns.owners.join(',');
	}

	if (unknowns.types.length > 0) {
		result.rt = unknowns.types.join(',');
	}

	if (unknowns.prefixes.length > 0) {
		result.ap = unknowns.prefixes.join(',');
	}

	return result;
}

// =============================================================================
// Main Export
// =============================================================================

/**
 * Encode app menu configuration into delimiter-based format with query parameters
 *
 * This function performs the following steps:
 * 1. Validates the input configuration
 * 2. Encodes each section based on its type
 * 3. Encodes ATIs to 3-byte binary then Base64URL
 * 4. Collects unknown ATI components for query parameters
 * 5. Produces delimiter-based string: v2~section1~section2~...
 * 6. Base64URL-encodes the entire string for URL transmission
 *
 * @private This function is strictly Universal Create internal - not for public use
 * @param config - App menu configuration to encode
 * @returns Encoded configuration with binary data and query parameters
 * @throws Error if limits are exceeded or format is invalid
 *
 * @example
 * ```typescript
 * const config = encodeConfig({
 *   appMenuConfig: [
 *     {
 *       type: 'appDefinedSection',
 *       items: [{ type: 'link', ati: 'ati:cloud:jira:issue' }],
 *       personalize: 'full'
 *     }
 *   ]
 * });
 * // Returns: { d: "djJ+...", ro: undefined, rt: undefined, ap: undefined }
 * ```
 */
export function encodeConfig(config: AppMenuConfig): EncodedConfig {
	validateConfig(config);

	const unknowns: UnknownComponents = { owners: [], types: [], prefixes: [] };

	const encodedSections = config.appMenuConfig.map((section) => encodeSection(section, unknowns));

	const rawConfigString = buildRawConfigString(encodedSections);

	// Encode UTF-8 string to bytes, then to base64url
	const utf8Bytes =
		typeof TextEncoder !== undefined
			? new TextEncoder().encode(rawConfigString)
			: new Uint8Array(Buffer.from(rawConfigString, 'utf-8'));
	const encodedData = encodeBase64Url(utf8Bytes);

	return attachQueryParameters(encodedData, unknowns);
}
